import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  UserPlus, User, Briefcase, Building2, Eye, EyeOff, CheckCircle, 
  Loader2, ArrowLeft, GraduationCap, Mail, Phone, Lock, MapPin,
  Sparkles, ChevronRight, FileText, Shield, Globe, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role configurations
const ROLES = [
  { 
    id: 'student', 
    label: 'Student / Learner', 
    icon: GraduationCap, 
    gradient: 'from-blue-500 to-cyan-500',
    description: 'Learn skills & get certified'
  },
  { 
    id: 'center', 
    label: 'Training Center', 
    icon: Building2, 
    gradient: 'from-purple-500 to-pink-500',
    description: 'Register your training institute'
  },
  { 
    id: 'employer', 
    label: 'Employer / Company', 
    icon: Briefcase, 
    gradient: 'from-emerald-500 to-teal-500',
    description: 'Hire skilled professionals'
  }
];

const UnifiedRegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useContext(AuthContext);
  
  const initialRole = searchParams.get('role') || 'student';
  
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const [step, setStep] = useState(1); // 1: Role, 2: OTP (for student), 3: Details
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [centers, setCenters] = useState([]);
  
  // Form data for different roles
  const [studentForm, setStudentForm] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: '',
    qualification: '',
    enrollment_mode: 'online',
    center_id: ''
  });
  
  const [centerForm, setCenterForm] = useState({
    center_name: '',
    owner_name: '',
    mobile: '',
    email: '',
    address: '',
    city: '',
    state: '',
    infrastructure: '',
    password: '',
    confirmPassword: ''
  });
  
  const [employerForm, setEmployerForm] = useState({
    company_name: '',
    contact_person: '',
    mobile: '',
    email: '',
    company_address: '',
    industry: '',
    password: '',
    confirmPassword: ''
  });

  // Get role config
  const roleConfig = ROLES.find(r => r.id === selectedRole) || ROLES[0];
  const RoleIcon = roleConfig.icon;

  // Fetch centers for student registration
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await axios.get(`${API}/public/centers`);
        setCenters(response.data || []);
      } catch (error) {
        console.log('Could not fetch centers');
      }
    };
    fetchCenters();
  }, []);

  // Send OTP for student
  const handleSendOTP = async () => {
    if (studentForm.mobile.length !== 10) {
      toast.error('Please enter valid 10-digit mobile number');
      return;
    }
    
    setIsLoading(true);
    try {
      // Check if mobile exists
      try {
        const checkRes = await axios.post(`${API}/auth/check-mobile`, { 
          phone: `+91${studentForm.mobile}` 
        });
        if (checkRes.data.exists) {
          toast.error('Mobile number already registered. Please login.');
          setIsLoading(false);
          return;
        }
      } catch {}
      
      const response = await axios.post(`${API}/auth/send-otp`, {
        mobile: studentForm.mobile
      });
      
      setOtpSent(true);
      toast.success('OTP sent to +91' + studentForm.mobile);
      
      // Demo mode
      if (response.data.otp) {
        toast.info(`Demo OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (otp.length !== 4) {
      toast.error('Please enter 4-digit OTP');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        mobile: studentForm.mobile,
        otp: otp
      });
      
      setTempToken(response.data.temp_token);
      setStep(3);
      toast.success('Mobile verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Register Student
  const handleStudentRegister = async () => {
    if (!studentForm.name || !studentForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (studentForm.password !== studentForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (studentForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-student`, {
        temp_token: tempToken,
        name: studentForm.name,
        email: studentForm.email || undefined,
        password: studentForm.password,
        mobile: studentForm.mobile  // Add mobile number from form
      });
      
      login(response.data.user, response.data.token);
      toast.success('Registration successful! Welcome to Iqra Skills!');
      navigate('/student/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      // Handle validation errors (array format)
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Registration failed');
      } else {
        toast.error(errorMsg || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Register Employer
  const handleEmployerRegister = async () => {
    if (!employerForm.company_name || !employerForm.contact_person || !employerForm.email || !employerForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (employerForm.password !== employerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-employer`, {
        company_name: employerForm.company_name,
        contact_person: employerForm.contact_person,
        email: employerForm.email,
        phone: employerForm.mobile ? `+91${employerForm.mobile}` : undefined,
        company_address: employerForm.company_address,
        industry: employerForm.industry,
        password: employerForm.password
      });
      
      login(response.data.user, response.data.token);
      toast.success('Registration successful! Welcome to Iqra Skills!');
      navigate('/employer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Register Training Center (Redirects to Partner Apply)
  const handleCenterRegister = () => {
    navigate('/partner-apply');
  };

  // Render Student Registration
  const renderStudentForm = () => {
    if (step === 2 && !otpSent) {
      // Mobile Entry
      return (
        <div className="space-y-4">
          <div>
            <Label className="text-white/80">Mobile Number *</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <div className="absolute left-10 top-1/2 -translate-y-1/2 text-white/60">+91</div>
              <Input
                type="tel"
                maxLength={10}
                value={studentForm.mobile}
                onChange={(e) => setStudentForm({ ...studentForm, mobile: e.target.value.replace(/\D/g, '') })}
                placeholder="Enter 10-digit mobile"
                className="pl-20 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
              />
            </div>
          </div>
          
          <Button
            onClick={handleSendOTP}
            disabled={isLoading || studentForm.mobile.length !== 10}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send OTP'}
          </Button>
        </div>
      );
    }
    
    if (step === 2 && otpSent) {
      // OTP Verification
      return (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <p className="text-white/80">Enter OTP sent to</p>
            <p className="text-white font-semibold">+91 {studentForm.mobile}</p>
          </div>
          
          <div>
            <Label className="text-white/80">Enter OTP</Label>
            <Input
              type="text"
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="4-digit OTP"
              className="text-center text-2xl tracking-widest bg-white/10 border-white/20 text-white rounded-xl h-14"
            />
          </div>
          
          <Button
            onClick={handleVerifyOTP}
            disabled={isLoading || otp.length !== 4}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify OTP'}
          </Button>
          
          <button 
            onClick={() => { setOtpSent(false); setOtp(''); }}
            className="text-blue-400 text-sm w-full text-center"
          >
            Change mobile number
          </button>
        </div>
      );
    }
    
    if (step === 3) {
      // Final Details
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-emerald-400 mb-4">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">Mobile verified: +91 {studentForm.mobile}</span>
          </div>
          
          <div>
            <Label className="text-white/80">Full Name *</Label>
            <Input
              value={studentForm.name}
              onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
              placeholder="Enter your full name"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
            />
          </div>
          
          <div>
            <Label className="text-white/80">Email (Optional)</Label>
            <Input
              type="email"
              value={studentForm.email}
              onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
              placeholder="your@email.com"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/80">Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={studentForm.password}
                  onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                  placeholder="Min 6 chars"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/80">Confirm *</Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={studentForm.confirmPassword}
                onChange={(e) => setStudentForm({ ...studentForm, confirmPassword: e.target.value })}
                placeholder="Confirm password"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
              />
            </div>
          </div>
          
          <label className="flex items-center gap-2 text-white/60 text-sm">
            <input type="checkbox" onChange={() => setShowPassword(!showPassword)} />
            Show password
          </label>
          
          <Button
            onClick={handleStudentRegister}
            disabled={isLoading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
          </Button>
        </div>
      );
    }
    
    return null;
  };

  // Render Employer Registration
  const renderEmployerForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-white/80">Company Name *</Label>
          <Input
            value={employerForm.company_name}
            onChange={(e) => setEmployerForm({ ...employerForm, company_name: e.target.value })}
            placeholder="Company name"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div>
          <Label className="text-white/80">Contact Person *</Label>
          <Input
            value={employerForm.contact_person}
            onChange={(e) => setEmployerForm({ ...employerForm, contact_person: e.target.value })}
            placeholder="Full name"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div>
          <Label className="text-white/80">Mobile</Label>
          <Input
            type="tel"
            maxLength={10}
            value={employerForm.mobile}
            onChange={(e) => setEmployerForm({ ...employerForm, mobile: e.target.value.replace(/\D/g, '') })}
            placeholder="10-digit mobile"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div className="col-span-2">
          <Label className="text-white/80">Email *</Label>
          <Input
            type="email"
            value={employerForm.email}
            onChange={(e) => setEmployerForm({ ...employerForm, email: e.target.value })}
            placeholder="company@email.com"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div>
          <Label className="text-white/80">Industry</Label>
          <select
            value={employerForm.industry}
            onChange={(e) => setEmployerForm({ ...employerForm, industry: e.target.value })}
            className="w-full h-12 px-3 bg-white/10 border border-white/20 text-white rounded-xl"
          >
            <option value="" className="bg-slate-800">Select industry</option>
            <option value="IT" className="bg-slate-800">IT / Software</option>
            <option value="Manufacturing" className="bg-slate-800">Manufacturing</option>
            <option value="Healthcare" className="bg-slate-800">Healthcare</option>
            <option value="Education" className="bg-slate-800">Education</option>
            <option value="Finance" className="bg-slate-800">Finance / Banking</option>
            <option value="Retail" className="bg-slate-800">Retail</option>
            <option value="Other" className="bg-slate-800">Other</option>
          </select>
        </div>
        
        <div>
          <Label className="text-white/80">Company Address</Label>
          <Input
            value={employerForm.company_address}
            onChange={(e) => setEmployerForm({ ...employerForm, company_address: e.target.value })}
            placeholder="City, State"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div>
          <Label className="text-white/80">Password *</Label>
          <Input
            type={showPassword ? 'text' : 'password'}
            value={employerForm.password}
            onChange={(e) => setEmployerForm({ ...employerForm, password: e.target.value })}
            placeholder="Min 6 chars"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
        
        <div>
          <Label className="text-white/80">Confirm Password *</Label>
          <Input
            type={showPassword ? 'text' : 'password'}
            value={employerForm.confirmPassword}
            onChange={(e) => setEmployerForm({ ...employerForm, confirmPassword: e.target.value })}
            placeholder="Confirm"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
          />
        </div>
      </div>
      
      <label className="flex items-center gap-2 text-white/60 text-sm">
        <input type="checkbox" onChange={() => setShowPassword(!showPassword)} />
        Show password
      </label>
      
      <Button
        onClick={handleEmployerRegister}
        disabled={isLoading}
        className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500"
      >
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Register Company'}
      </Button>
    </div>
  );

  // Render Training Center Registration
  const renderCenterForm = () => (
    <div className="space-y-4">
      <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-4 text-center">
        <Building2 className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h3 className="text-white font-semibold mb-2">Become a Partner</h3>
        <p className="text-white/70 text-sm mb-4">
          Register your training center as an Iqra Skills partner. 
          Choose from State Partner, District Center, or Training Center options.
        </p>
        <Button
          onClick={handleCenterRegister}
          className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl"
        >
          Apply as Partner <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
      
      <div className="text-center text-white/50 text-sm">
        <p>Already registered?</p>
        <Link to="/auth/login?role=center" className="text-purple-400 hover:text-purple-300">
          Login to Center Dashboard
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 via-transparent to-emerald-500/10"></div>
      </div>
      
      <div className="relative max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              <span className="text-blue-400">Iqra</span>
              <span className="text-emerald-400"> Skills</span>
            </h1>
          </Link>
          <p className="text-slate-400 mt-2">Create your account</p>
        </div>

        {/* Main Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Role Selector - Only show on step 1 */}
          {step === 1 && (
            <div className="p-6 border-b border-white/10">
              <Label className="text-white/80 text-sm mb-3 block">I want to register as</Label>
              <div className="space-y-2">
                {ROLES.map(role => {
                  const Icon = role.icon;
                  const isSelected = selectedRole === role.id;
                  return (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`w-full p-4 rounded-xl border transition-all text-left flex items-center gap-4 ${
                        isSelected 
                          ? `bg-gradient-to-r ${role.gradient} border-transparent text-white` 
                          : 'border-white/20 hover:border-white/40 text-white/70 hover:text-white'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-white/20' : 'bg-white/10'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold">{role.label}</p>
                        <p className={`text-sm ${isSelected ? 'text-white/80' : 'text-white/50'}`}>
                          {role.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <Button
                onClick={() => setStep(2)}
                className={`w-full mt-4 h-12 rounded-xl bg-gradient-to-r ${roleConfig.gradient}`}
              >
                Continue <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Registration Forms */}
          {step > 1 && (
            <div className="p-6">
              {/* Back Button */}
              <button
                onClick={() => {
                  if (step === 2 && !otpSent) setStep(1);
                  else if (step === 2 && otpSent) { setOtpSent(false); setOtp(''); }
                  else if (step === 3) setStep(2);
                }}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              
              {/* Role Header */}
              <div className={`p-3 rounded-xl bg-gradient-to-r ${roleConfig.gradient} bg-opacity-20 mb-6`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <RoleIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{roleConfig.label} Registration</p>
                    <p className="text-white/70 text-xs">
                      {selectedRole === 'student' && step === 2 && !otpSent && 'Step 1: Verify Mobile'}
                      {selectedRole === 'student' && step === 2 && otpSent && 'Step 2: Enter OTP'}
                      {selectedRole === 'student' && step === 3 && 'Step 3: Complete Profile'}
                      {selectedRole === 'employer' && 'Fill company details'}
                      {selectedRole === 'center' && 'Partner registration'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Forms */}
              {selectedRole === 'student' && renderStudentForm()}
              {selectedRole === 'employer' && renderEmployerForm()}
              {selectedRole === 'center' && renderCenterForm()}
            </div>
          )}

          {/* Login Link */}
          <div className="p-6 pt-0 text-center">
            <p className="text-white/50 text-sm">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-blue-400 hover:text-blue-300 font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6">
          © 2026 Iqra Skills Pvt Ltd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default UnifiedRegisterPage;
